import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "../App";
import { Loader2, Printer } from "lucide-react";
import { Button } from "../components/ui/button";

const STATUS_LABEL = {
  operational: "Operational",
  needs_maintenance: "Needs Maintenance",
  out_of_service: "Out of Service",
};

const BiomedDevicePrint = () => {
  const { deviceId } = useParams();
  const { token } = useAuth();
  const [device, setDevice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sigs, setSigs] = useState(null);
  const [sigImgs, setSigImgs] = useState({});
  const [maintHistory, setMaintHistory] = useState([]);

  useEffect(() => {
    const headers = { Authorization: `Bearer ${token}` };
    const load = async () => {
      try {
        const res = await axios.get(`${API}/biomed-devices/${deviceId}`, { headers });
        setDevice(res.data);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
      try {
        const sres = await axios.get(`${API}/biomed-devices/${deviceId}/report-signatures`, { headers });
        setSigs(sres.data);
        const imgs = {};
        for (const key of ["evaluated_by", "approved_by"]) {
          const b = sres.data[key];
          if (b?.has_signature && !imgs[b.user_id]) {
            try {
              const img = await axios.get(`${API}/users/${b.user_id}/signature-image`, { headers, responseType: "blob" });
              imgs[b.user_id] = URL.createObjectURL(img.data);
            } catch { /* no image */ }
          }
        }
        setSigImgs(imgs);
      } catch { /* signatures optional */ }
      try {
        const mres = await axios.get(`${API}/biomed-devices/${deviceId}/maintenance-history`, { headers });
        setMaintHistory(mres.data || []);
      } catch { /* history optional */ }
    };
    load();
  }, [deviceId, token]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <Loader2 className="w-8 h-8 animate-spin text-[#655dc6]" />
      </div>
    );
  }

  if (!device) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <p className="text-slate-500">Device not found.</p>
      </div>
    );
  }

  const evaluations = device.evaluations || [];

  return (
    <div dir="ltr" className="min-h-screen bg-white text-slate-900 print-page">
      {/* Print styles */}
      <style>{`
        @media print {
          @page { size: A4; margin: 15mm; }
          .no-print { display: none !important; }
          /* Restore visibility (overrides App.css global rule that hides everything on print) */
          .print-page, .print-page * { visibility: visible !important; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
        .print-page { font-family: 'Segoe UI', Tahoma, Arial, sans-serif; }
      `}</style>

      <div className="max-w-4xl mx-auto p-8">
        {/* Print button (hidden when printing) */}
        <div className="no-print flex justify-end mb-4">
          <Button onClick={() => window.print()} className="bg-[#655dc6] hover:bg-[#5449a8] text-white gap-2" data-testid="print-eval-btn">
            <Printer className="w-4 h-4" /> Print
          </Button>
        </div>

        {/* Header with Lucent branding */}
        <div className="flex items-center justify-between border-b-2 border-[#655dc6] pb-4 mb-6">
          <div className="flex items-center gap-4">
            <img src="/lucent-logo.png" alt="Lucent" className="h-14" />
            <div>
              <h1 className="text-xl font-bold text-[#4a3fa8]">Lucent Dental Center Company</h1>
              <p className="text-sm text-slate-500">Semi-Annual Device Evaluation Report</p>
            </div>
          </div>
          <div className="text-right text-xs text-slate-500">
            <p>Report Date: {new Date().toISOString().split("T")[0]}</p>
            <p className="font-mono">Ref: EVAL-{device.biomed_no}</p>
          </div>
        </div>

        {/* Device details */}
        <section className="mb-6">
          <h2 className="text-base font-semibold uppercase tracking-wider text-[#655dc6] border-b border-slate-200 pb-1 mb-3">
            Device Information
          </h2>
          <table className="w-full text-sm">
            <tbody>
              <tr className="border-b border-slate-100">
                <td className="py-2 text-slate-500 w-1/3">Device Type</td>
                <td className="py-2 font-medium capitalize">{device.device_type || "biomed"}{device.electrical_type ? ` (${device.electrical_type})` : ""}{device.electric_type ? ` (${device.electric_type})` : ""}</td>
              </tr>
              <tr className="border-b border-slate-100">
                <td className="py-2 text-slate-500">{(device.device_type === "electrical" || device.device_type === "electric") ? "Asset No." : "BioMed No."}</td>
                <td className="py-2 font-mono font-semibold text-[#4a3fa8]">{device.biomed_no}</td>
              </tr>
              <tr className="border-b border-slate-100">
                <td className="py-2 text-slate-500">Device Name</td>
                <td className="py-2 font-medium">{device.device_name}</td>
              </tr>
              <tr className="border-b border-slate-100">
                <td className="py-2 text-slate-500">Manufacturer</td>
                <td className="py-2">{device.manufacturer}</td>
              </tr>
              <tr className="border-b border-slate-100">
                <td className="py-2 text-slate-500">Model No.</td>
                <td className="py-2 font-mono">{device.model_no}</td>
              </tr>
              <tr className="border-b border-slate-100">
                <td className="py-2 text-slate-500">Serial No.</td>
                <td className="py-2 font-mono">{device.serial_no}</td>
              </tr>
              <tr className="border-b border-slate-100">
                <td className="py-2 text-slate-500">Location</td>
                <td className="py-2">
                  <span className="capitalize">[{device.location_type || "—"}]</span> {device.location_name || device.clinic_name || "—"}
                  {(device.location_floor || device.clinic_floor) && ` — Floor ${device.location_floor || device.clinic_floor}`}
                </td>
              </tr>
              <tr>
                <td className="py-2 text-slate-500">Current Status</td>
                <td className="py-2 font-semibold">{STATUS_LABEL[device.latest_status] || "Not Evaluated Yet"}</td>
              </tr>
            </tbody>
          </table>
        </section>

        {/* Transfer history */}
        {(device.transfer_history || []).length > 0 && (
          <section className="mb-6">
            <h2 className="text-base font-semibold uppercase tracking-wider text-[#655dc6] border-b border-slate-200 pb-1 mb-3">
              Transfer History / سجل التنقلات
            </h2>
            <table className="w-full text-sm border border-slate-300">
              <thead>
                <tr className="bg-[#F5F3F9] text-[#4a3fa8]">
                  <th className="border border-slate-300 p-2 text-left">Date</th>
                  <th className="border border-slate-300 p-2 text-left">From</th>
                  <th className="border border-slate-300 p-2 text-left">To</th>
                  <th className="border border-slate-300 p-2 text-left">By</th>
                  <th className="border border-slate-300 p-2 text-left">Notes</th>
                </tr>
              </thead>
              <tbody>
                {[...device.transfer_history].reverse().map((tr, i) => (
                  <tr key={tr.id || i}>
                    <td className="border border-slate-300 p-2 text-xs">{(tr.transferred_at || "").slice(0, 10)}</td>
                    <td className="border border-slate-300 p-2 text-xs">{tr.from_name || "—"}</td>
                    <td className="border border-slate-300 p-2 text-xs font-medium">{tr.to_name || "—"}</td>
                    <td className="border border-slate-300 p-2 text-xs">{tr.transferred_by_name || "—"}</td>
                    <td className="border border-slate-300 p-2 text-xs">{tr.notes || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}

        {/* Maintenance history */}
        {maintHistory.length > 0 && (
          <section className="mb-6">
            <h2 className="text-base font-semibold uppercase tracking-wider text-[#655dc6] border-b border-slate-200 pb-1 mb-3">
              Maintenance History / سجل الصيانة ({maintHistory.length})
            </h2>
            <table className="w-full text-sm border border-slate-300">
              <thead>
                <tr className="bg-[#F5F3F9] text-[#4a3fa8]">
                  <th className="border border-slate-300 p-2 text-left">Date</th>
                  <th className="border border-slate-300 p-2 text-left">Description</th>
                  <th className="border border-slate-300 p-2 text-left">Location</th>
                  <th className="border border-slate-300 p-2 text-left">Assigned To</th>
                  <th className="border border-slate-300 p-2 text-left">Status</th>
                  <th className="border border-slate-300 p-2 text-left">Completed</th>
                </tr>
              </thead>
              <tbody>
                {maintHistory.map((m, i) => (
                  <tr key={m.id || i}>
                    <td className="border border-slate-300 p-2 text-xs whitespace-nowrap">{(m.created_at || "").slice(0, 10)}</td>
                    <td className="border border-slate-300 p-2 text-xs" dir="auto">{m.description || "—"}</td>
                    <td className="border border-slate-300 p-2 text-xs" dir="auto">{m.clinic_name || m.location_name || "—"}</td>
                    <td className="border border-slate-300 p-2 text-xs" dir="auto">{m.assigned_to || "—"}</td>
                    <td className="border border-slate-300 p-2 text-xs">
                      {m.status === "completed" ? "Completed / مكتمل" : m.status === "in_progress" ? "In Progress / جاري" : m.status === "pending" ? "Pending / معلق" : m.status}
                    </td>
                    <td className="border border-slate-300 p-2 text-xs whitespace-nowrap">{(m.completed_at || "").slice(0, 10) || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}

        {/* Evaluation history */}
        <section className="mb-6">
          <h2 className="text-base font-semibold uppercase tracking-wider text-[#655dc6] border-b border-slate-200 pb-1 mb-3">
            Evaluation History
          </h2>
          {evaluations.length === 0 ? (
            <p className="text-slate-500 italic text-sm">No evaluations recorded yet.</p>
          ) : (
            <table className="w-full text-sm border border-slate-300">
              <thead>
                <tr className="bg-[#F5F3F9] text-[#4a3fa8]">
                  <th className="border border-slate-300 p-2 text-left">Date</th>
                  <th className="border border-slate-300 p-2 text-left">Status</th>
                  <th className="border border-slate-300 p-2 text-left">Score</th>
                  <th className="border border-slate-300 p-2 text-left">Evaluated By</th>
                  <th className="border border-slate-300 p-2 text-left">Notes</th>
                </tr>
              </thead>
              <tbody>
                {[...evaluations].reverse().map((ev) => (
                  <tr key={ev.id}>
                    <td className="border border-slate-300 p-2 text-xs">
                      {ev.evaluated_at ? ev.evaluated_at.split("T")[0] : "—"}
                    </td>
                    <td className="border border-slate-300 p-2 font-medium">{STATUS_LABEL[ev.status] || ev.status}</td>
                    <td className="border border-slate-300 p-2">{ev.condition_score ?? "—"} / 5</td>
                    <td className="border border-slate-300 p-2 text-xs">{ev.evaluated_by_name || "—"}</td>
                    <td className="border border-slate-300 p-2 text-xs">{ev.notes || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>

        {/* Signatures */}
        <section className="mt-16 grid grid-cols-2 gap-12 text-sm">
          {[
            { label: "Evaluated By:", block: sigs?.evaluated_by, testid: "sig-evaluated-by" },
            { label: "Approved By:", block: sigs?.approved_by, testid: "sig-approved-by" },
          ].map(({ label, block, testid }) => (
            <div key={label} data-testid={testid}>
              <p className="text-slate-500 mb-2">{label}</p>
              {block?.name ? (
                <p className="font-semibold text-slate-800 mb-1">{block.name}</p>
              ) : (
                <div className="mb-6"></div>
              )}
              <div className="h-16 flex items-end">
                {block?.has_signature && sigImgs[block.user_id] ? (
                  <img src={sigImgs[block.user_id]} alt="signature" className="max-h-16 max-w-[220px] object-contain" />
                ) : null}
              </div>
              <div className="border-b border-slate-400 pb-1"></div>
              <p className="text-xs text-slate-400 mt-1">Name &amp; Signature</p>
            </div>
          ))}
        </section>

        <p className="text-center text-[10px] text-slate-400 mt-12 pt-4 border-t border-slate-200">
          © {new Date().getFullYear()} Lucent Dental Center Company — Semi-Annual Device Evaluation
        </p>
      </div>
    </div>
  );
};

export default BiomedDevicePrint;
